/* Botanica homepage motion.
 *
 * Deliberately GSAP-free. botanica-film.js bundles GSAP 3.15.0 + ScrollTrigger inside an IIFE
 * ((function(){"use strict"; ... })();) so nothing it loads is reachable from this file — verified
 * on the live page: window.gsap === undefined while window.gsapVersions === ["3.15.0"]. Loading a
 * second GSAP would ship ~120KB twice, so everything here is hand-rolled against
 * IntersectionObserver + one shared rAF loop. Only external dependency is Lenis (5KB gz, no deps).
 *
 * Hard rule: content is NEVER hidden by JS. Every animated element is readable with JS disabled,
 * with this file blocked, and under prefers-reduced-motion. We animate from a visible baseline —
 * the CSS reveal state only applies once <html> gets .bb-motion, which we set below.
 */
(function () {
  'use strict';

  var root = document.documentElement;
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Reduced motion: mark it and stop. No Lenis, no observers, no rAF. Static page, fully readable.
  if (reduce) {
    root.classList.add('bb-motion-off');
    return;
  }

  root.classList.add('bb-motion');

  /* ---------------------------------------------------------------- shared rAF
   * One loop for everything. Farm Minerals and Moooi both do this; separate rAF loops per
   * component is how scroll pages end up dropping frames.
   */
  var tasks = [];
  var running = false;

  function tick(now) {
    for (var i = 0; i < tasks.length; i++) tasks[i](now);
    if (running) requestAnimationFrame(tick);
  }
  function addTask(fn) {
    tasks.push(fn);
    if (!running) {
      running = true;
      requestAnimationFrame(tick);
    }
  }

  /* ---------------------------------------------------------------- smooth scroll
   * lerp 0.1 matches the Farm Minerals reference. Touch stays native — syncTouch defaults false
   * and smoothed touch scrolling feels broken on iOS.
   */
  var lenis = null;
  if (typeof window.Lenis === 'function') {
    lenis = new window.Lenis({ lerp: 0.1 });
    window.lenis = lenis;
    addTask(function (now) { lenis.raf(now); });
  }

  /* ---------------------------------------------------------------- reveals
   * fade + rise on enter, once. Children stagger via --bb-i on each child, consumed by CSS
   * transition-delay, so the stagger costs zero JS per item.
   */
  function initReveals() {
    var nodes = document.querySelectorAll('[data-bb-reveal]');
    if (!nodes.length) return;

    // Stamp child indices up front so CSS can delay them.
    for (var n = 0; n < nodes.length; n++) {
      var kids = nodes[n].querySelectorAll('[data-bb-stagger] > *');
      for (var k = 0; k < kids.length; k++) kids[k].style.setProperty('--bb-i', k);
    }

    if (!('IntersectionObserver' in window)) {
      // No IO: reveal everything immediately rather than leaving it mid-transition.
      for (var f = 0; f < nodes.length; f++) nodes[f].classList.add('is-in');
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (!entries[i].isIntersecting) continue;
        entries[i].target.classList.add('is-in');
        io.unobserve(entries[i].target);
      }
    }, { rootMargin: '0px 0px -12% 0px', threshold: 0.01 });

    for (var j = 0; j < nodes.length; j++) io.observe(nodes[j]);
  }

  /* ---------------------------------------------------------------- split headlines
   * Two systems on purpose, same as Farm Minerals: display headlines dissolve per character,
   * body/lead copy slides up per word behind a clip. Costs ~15 lines instead of the SplitText
   * plugin (which is GSAP-scoped and unreachable anyway).
   *
   * Accessibility: the original text stays as a visually-hidden first child (.bb-sr-only), and
   * the generated spans are aria-hidden, so a screen reader reads one clean string, not letters.
   */
  function splitText(el, mode) {
    var text = el.textContent;
    if (!text || el.dataset.bbSplitDone) return;
    el.dataset.bbSplitDone = '1';
    /* aria-label is prohibited on generic spans/paragraphs (axe aria-prohibited-attr); keep the
       clean string as a visually-hidden text node instead, and hide the split chars. */
    var sr = document.createElement('span');
    sr.className = 'bb-sr-only';
    sr.textContent = text.trim();

    var units = mode === 'words' ? text.split(/(\s+)/) : text.split('');
    var frag = document.createDocumentFragment();
    frag.appendChild(sr);
    var idx = 0;

    for (var i = 0; i < units.length; i++) {
      var u = units[i];
      if (/^\s+$/.test(u)) { frag.appendChild(document.createTextNode(u)); continue; }
      var outer = document.createElement('span');
      outer.className = mode === 'words' ? 'bb-word' : 'bb-char';
      outer.setAttribute('aria-hidden', 'true');
      outer.style.setProperty('--bb-i', idx++);
      outer.textContent = u;
      frag.appendChild(outer);
    }

    el.textContent = '';
    el.appendChild(frag);
    el.classList.add(mode === 'words' ? 'bb-split-words' : 'bb-split-chars');
  }

  function initSplits() {
    var chars = document.querySelectorAll('[data-bb-split="chars"]');
    /* Every char is an atomic inline box, so a char-split heading can wrap mid-word
       ("LOVED BY CUSTOM / ERS") on narrow screens. Words are the unit on mobile, always. */
    var wholeWordsOnMobile = window.innerWidth <= 760;
    for (var i = 0; i < chars.length; i++) {
      splitText(chars[i], wholeWordsOnMobile ? 'words' : 'chars');
    }
    var words = document.querySelectorAll('[data-bb-split="words"]');
    for (var j = 0; j < words.length; j++) splitText(words[j], 'words');
  }

  /* ---------------------------------------------------------------- scroll-linked
   * Generic: an element declares how far to travel and over which trigger, we write a CSS
   * custom property 0..1. All actual movement lives in CSS, so nothing here touches layout.
   *
   * [data-bb-scrub] on the trigger, [data-bb-scrub-var] names the property (default --bb-p).
   */
  function initScrub() {
    var nodes = document.querySelectorAll('[data-bb-scrub]');
    if (!nodes.length) return;

    var items = [];
    for (var i = 0; i < nodes.length; i++) {
      items.push({ el: nodes[i], prop: nodes[i].dataset.bbScrubVar || '--bb-p', visible: false, last: -1 });
    }

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        for (var e = 0; e < entries.length; e++) {
          for (var m = 0; m < items.length; m++) {
            if (items[m].el === entries[e].target) items[m].visible = entries[e].isIntersecting;
          }
        }
      }, { rootMargin: '20% 0px 20% 0px' });
      for (var o = 0; o < items.length; o++) io.observe(items[o].el);
    } else {
      for (var v = 0; v < items.length; v++) items[v].visible = true;
    }

    addTask(function () {
      var vh = window.innerHeight;
      for (var i = 0; i < items.length; i++) {
        var it = items[i];
        if (!it.visible) continue;
        var r = it.el.getBoundingClientRect();
        // 0 when the element's top hits the viewport bottom, 1 when its bottom hits the top.
        var span = r.height + vh;
        var p = span > 0 ? (vh - r.top) / span : 0;
        p = p < 0 ? 0 : p > 1 ? 1 : p;
        var q = Math.round(p * 1000) / 1000;
        if (q !== it.last) {
          it.last = q;
          it.el.style.setProperty(it.prop, q);
        }
      }
    });
  }

  /* ---------------------------------------------------------------- hero camera push
   * The approved rainforest stays one lightweight still. Scroll supplies the depth: as the first
   * viewport leaves, the image moves from 1× to 1.12× (1.08× on mobile), so the visitor travels
   * into the valley before Best Sellers takes over. No pin, frame sequence or extra request.
   */
  function initHeroZoom() {
    var hero = document.querySelector('[data-bb-hero-zoom]');
    if (!hero || hero.dataset.bbHeroZoomReady) return;
    hero.dataset.bbHeroZoomReady = '1';

    var media = hero.querySelector('.bb-home-hero__media img');
    if (!media) return;

    var visible = true;
    var last = -1;

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        visible = entries[0].isIntersecting;
      }, { rootMargin: '5% 0px 5% 0px' }).observe(hero);
    }

    addTask(function () {
      if (!visible) return;

      var rect = hero.getBoundingClientRect();
      var distance = Math.max(rect.height * 0.92, 1);
      var progress = -rect.top / distance;
      progress = progress < 0 ? 0 : progress > 1 ? 1 : progress;
      var rounded = Math.round(progress * 1000) / 1000;
      if (rounded === last) return;

      last = rounded;
      var depth = window.innerWidth <= 768 ? 0.08 : 0.12;
      hero.style.setProperty('--bb-hero-zoom', (1 + rounded * depth).toFixed(4));
      media.classList.toggle('is-zooming', rounded > 0 && rounded < 1);
    });
  }

  /* ---------------------------------------------------------------- header recolour
   * Repaints the fixed header when it overlaps a dark band. ~30 lines, and it is most of why a
   * page that alternates light/dark sections reads as bespoke rather than templated.
   */
  function initHeaderRecolour() {
    var header = document.querySelector('.bb-header');
    if (!header) return;
    var darks = document.querySelectorAll('[data-bb-band="dark"]');
    var hero = document.querySelector('[data-bb-hero]');

    var queued = false;
    var onDark = null;
    var overHero = null;
    var lastH = 0;

    function measure() {
      queued = false;
      var hb = header.getBoundingClientRect();

      /* Publish the header's real height so the hero can sit underneath it. The CSS fallback is
       * close enough that a JS failure costs a few pixels of alignment, never a broken page. */
      var h = Math.round(hb.height);
      if (h && h !== lastH) {
        lastH = h;
        root.style.setProperty('--bb-headerh', h + 'px');
      }

      /* Transparent while the hero is still behind the header, solid once it has scrolled past.
       * Checked against the hero's bottom edge, not a fixed scroll offset, so it stays correct
       * whatever height the hero ends up being. */
      if (hero) {
        var over = hero.getBoundingClientRect().bottom > hb.bottom;
        if (over !== overHero) {
          overHero = over;
          header.classList.toggle('bb-header--over-hero', over);
        }
      }

      if (!darks.length) return;
      var mid = hb.top + hb.height / 2;
      var hit = false;
      for (var i = 0; i < darks.length; i++) {
        var r = darks[i].getBoundingClientRect();
        if (r.top <= mid && r.bottom > mid) { hit = true; break; }
      }
      if (hit !== onDark) {
        onDark = hit;
        header.classList.toggle('bb-header--on-dark', hit);
      }
    }
    function schedule() {
      if (queued) return;
      queued = true;
      requestAnimationFrame(measure);
    }

    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });
    measure();
  }

  /* ---------------------------------------------------------------- philosophy droplet
   * The Three.js module owns water assembly and deformation. This shared loop owns only the
   * section-level scroll drift, a very small container response, and connector measurement.
   * Touch, keyboard, no-JS and reduced-motion states remain static and readable.
   */
  function initActivesMotion() {
    var section = document.querySelector('[data-bb-actives]');
    if (!section || section.dataset.bbActivesReady) return;
    section.dataset.bbActivesReady = '1';

    var stage = section.querySelector('[data-bb-actives-stage]');
    if (!stage) return;
    var media = section.querySelector('[data-bb-actives-media]');
    var hasWaterRenderer = !!section.querySelector('[data-bb-actives-water]');
    var names = section.querySelectorAll('.bb-actives__name');
    var connectors = section.querySelectorAll('[data-bb-actives-connector]');
    var wordDots = section.querySelectorAll('[data-bb-actives-word-dot]');
    var waterDots = section.querySelectorAll('[data-bb-actives-water-dot]');

    var visible = false;
    var last = '';
    var pointerFine = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    var pointerTargetX = 0;
    var pointerTargetY = 0;
    var pointerTargetRotate = 0;
    var pointerX = 0;
    var pointerY = 0;
    var pointerRotate = 0;

    function resetPointer() {
      pointerTargetX = 0;
      pointerTargetY = 0;
      pointerTargetRotate = 0;
    }

    if (pointerFine) {
      stage.addEventListener('pointermove', function (event) {
        var rect = stage.getBoundingClientRect();
        if (!rect.width || !rect.height) return;
        var nx = ((event.clientX - rect.left) / rect.width) * 2 - 1;
        var ny = ((event.clientY - rect.top) / rect.height) * 2 - 1;
        nx = nx < -1 ? -1 : nx > 1 ? 1 : nx;
        ny = ny < -1 ? -1 : ny > 1 ? 1 : ny;
        pointerTargetX = nx * (hasWaterRenderer ? 5 : 28);
        pointerTargetY = ny * (hasWaterRenderer ? 4 : 18);
        pointerTargetRotate = nx * (hasWaterRenderer ? 0.35 : 2.1);
      }, { passive: true });
      stage.addEventListener('pointerleave', resetPointer, { passive: true });
      stage.addEventListener('pointercancel', resetPointer, { passive: true });
    }

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        visible = entries[0].isIntersecting;
        section.classList.toggle('is-motion-active', visible);
      }, { rootMargin: '15% 0px 15% 0px' }).observe(section);
    } else {
      visible = true;
      section.classList.add('is-motion-active');
    }

    function updateConnectors() {
      if (!media || connectors.length !== names.length || wordDots.length !== names.length ||
          waterDots.length !== names.length || (window.innerWidth > 768 && window.innerWidth <= 900)) return;
      var stageRect = stage.getBoundingClientRect();
      var mediaRect = media.getBoundingClientRect();
      if (!stageRect.width || !stageRect.height || !mediaRect.width || !mediaRect.height) return;

      var centreX = mediaRect.left - stageRect.left + mediaRect.width / 2;
      var centreY = mediaRect.top - stageRect.top + mediaRect.height / 2;
      /* Each water dot is authored from Ethan's final overlay. Every connector follows STEM's
         exact two-segment grammar: a long horizontal run and one diagonal into the water. */
      var radius = Math.min(mediaRect.width, mediaRect.height) * 0.27;
      var mobileDiagram = window.innerWidth <= 768;
      /* The approved desktop anchors remain locked. Protect alone gets a phone-specific endpoint
         matching Ethan's overlay, which lengthens its horizontal run and makes the final segment
         nearly vertical without disturbing the other three connectors. */
      var anchorX = mobileDiagram ? [-0.56, 0.44, -0.82, 0.47] : [-0.78, 0.44, -0.82, 0.47];
      var anchorY = mobileDiagram ? [-0.73, -1.00, 0.84, 1.13] : [-0.67, -1.00, 0.84, 1.13];

      for (var i = 0; i < names.length; i++) {
        var nameRect = names[i].getBoundingClientRect();
        var onLeft = i === 0 || i === 2;
        var wordGap = mobileDiagram ? [8, 10, 10, 10][i] : 18;
        var startX = (onLeft ? nameRect.right + wordGap : nameRect.left - wordGap) - stageRect.left;
        var startY = nameRect.top - stageRect.top + nameRect.height / 2;
        var endX = centreX + anchorX[i] * radius;
        var endY = centreY + anchorY[i] * radius;
        var diagonalRun = (mobileDiagram ? [5, 12, 12, 12] : [38, 44, 38, 47])[i];
        var elbowX = endX + (onLeft ? -diagonalRun : diagonalRun);
        var points = [[startX, startY], [elbowX, startY], [endX, endY]];

        var svgPoints = [];
        for (var pointIndex = 0; pointIndex < points.length; pointIndex++) {
          svgPoints.push(
            (points[pointIndex][0] / stageRect.width * 1200).toFixed(1) + ',' +
            (points[pointIndex][1] / stageRect.height * 600).toFixed(1)
          );
        }
        connectors[i].setAttribute('points', svgPoints.join(' '));
        /* HTML dots use stage pixels rather than the SVG's stretched coordinate system. Their
           centres are the exact first and last polyline points, so no animation or aspect ratio
           can open a visible gap between marker and connector. */
        wordDots[i].style.left = startX.toFixed(1) + 'px';
        wordDots[i].style.top = startY.toFixed(1) + 'px';
        waterDots[i].style.left = endX.toFixed(1) + 'px';
        waterDots[i].style.top = endY.toFixed(1) + 'px';
      }
    }

    addTask(function (now) {
      if (!visible) return;

      var rect = section.getBoundingClientRect();
      var span = rect.height + window.innerHeight;
      var progress = span > 0 ? (window.innerHeight - rect.top) / span : 0.5;
      progress = progress < 0 ? 0 : progress > 1 ? 1 : progress;

      var mobile = window.innerWidth <= 768;
      var travel = mobile ? 28 : 64;
      var ambientX = Math.sin(now / 1450) * (mobile ? 1.5 : 4.5);
      var ambientY = Math.sin(now / 1120 + 0.8) * (mobile ? 2 : 6);
      var ambientRotate = Math.sin(now / 2100) * (mobile ? 0.2 : 0.55);
      var y = travel / 2 - progress * travel + ambientY;
      var rotation = -1.6 + progress * 3.2 + ambientRotate;
      var focus = 1 - Math.abs(progress * 2 - 1);
      var scale = 0.97 + focus * 0.035;

      pointerX += (pointerTargetX - pointerX) * 0.075;
      pointerY += (pointerTargetY - pointerY) * 0.075;
      pointerRotate += (pointerTargetRotate - pointerRotate) * 0.075;

      if (Math.abs(pointerTargetX - pointerX) < 0.01) pointerX = pointerTargetX;
      if (Math.abs(pointerTargetY - pointerY) < 0.01) pointerY = pointerTargetY;
      if (Math.abs(pointerTargetRotate - pointerRotate) < 0.001) pointerRotate = pointerTargetRotate;

      var signature = ambientX.toFixed(2) + '|' + y.toFixed(2) + '|' + rotation.toFixed(3) + '|' + scale.toFixed(4) +
        '|' + pointerX.toFixed(2) + '|' + pointerY.toFixed(2) + '|' + pointerRotate.toFixed(3);
      if (signature === last) return;

      last = signature;
      stage.style.setProperty('--bb-actives-y', y.toFixed(2) + 'px');
      stage.style.setProperty('--bb-actives-ambient-x', ambientX.toFixed(2) + 'px');
      stage.style.setProperty('--bb-actives-rotate', rotation.toFixed(3) + 'deg');
      stage.style.setProperty('--bb-actives-scale', scale.toFixed(4));
      stage.style.setProperty('--bb-actives-pointer-x', pointerX.toFixed(2) + 'px');
      stage.style.setProperty('--bb-actives-pointer-y', pointerY.toFixed(2) + 'px');
      stage.style.setProperty('--bb-actives-pointer-rotate', pointerRotate.toFixed(3) + 'deg');
      updateConnectors();
    });
  }

  /* ---------------------------------------------------------------- ritual traveler
   * Static chapter droplets remain the no-JS/reduced-motion fallback. With motion enabled, one
   * layered droplet travels continuously between their measured slots and changes product as the
   * scroll scanline moves through the four chapters.
   */
  function initRitual() {
    var section = document.querySelector('[data-bb-ritual]');
    if (!section || section.dataset.bbRitualReady) return;
    section.dataset.bbRitualReady = '1';

    var journey = section.querySelector('.bb-ritual__journey');
    var traveler = section.querySelector('[data-bb-ritual-traveler]');
    var stage = section.querySelector('[data-bb-ritual-stage]');
    var steps = section.querySelectorAll('[data-bb-ritual-step]');
    var slots = section.querySelectorAll('.bb-ritual__visual');
    var layers = stage ? stage.querySelectorAll('[data-bb-ritual-layer]') : [];
    if (!journey || !traveler || !stage || !steps.length ||
        slots.length !== steps.length || layers.length !== steps.length) return;

    var visible = false;
    var current = -1;
    var currentX = null;
    var currentY = null;
    var last = '';

    section.classList.add('is-traveler-ready');
    layers[0].classList.add('is-active');

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        visible = entries[0].isIntersecting;
        section.classList.toggle('is-motion-active', visible);
      }, { rootMargin: '10% 0px 10% 0px' }).observe(section);
    } else {
      visible = true;
      section.classList.add('is-motion-active');
    }

    addTask(function (now) {
      if (!visible) return;
      /* The scanline sits above centre so the droplet arrives with the chapter, not after it. */
      var line = window.innerHeight * (window.innerWidth <= 760 ? 0.5 : 0.45);
      var journeyRect = journey.getBoundingClientRect();
      var travelerRect = traveler.getBoundingClientRect();
      if (!journeyRect.width || !travelerRect.width || !travelerRect.height) return;

      var points = [];
      var mids = [];

      for (var i = 0; i < steps.length; i++) {
        var stepRect = steps[i].getBoundingClientRect();
        var slotRect = slots[i].getBoundingClientRect();
        mids.push(stepRect.top + stepRect.height / 2);
        points.push({
          x: slotRect.left - journeyRect.left + (slotRect.width - travelerRect.width) / 2,
          y: slotRect.top - journeyRect.top + (slotRect.height - travelerRect.height) / 2
        });
      }

      var from = 0;
      var mix = 0;
      if (line >= mids[mids.length - 1]) {
        from = mids.length - 1;
      } else if (line > mids[0]) {
        for (var m = 0; m < mids.length - 1; m++) {
          if (line <= mids[m + 1]) {
            from = m;
            mix = (line - mids[m]) / Math.max(mids[m + 1] - mids[m], 1);
            break;
          }
        }
      }

      var to = Math.min(from + 1, points.length - 1);
      var targetX = points[from].x + (points[to].x - points[from].x) * mix;
      var targetY = points[from].y + (points[to].y - points[from].y) * mix;
      if (currentX === null) {
        currentX = targetX;
        currentY = targetY;
      } else {
        currentX += (targetX - currentX) * 0.09;
        currentY += (targetY - currentY) * 0.09;
      }

      var best = mix < 0.5 ? from : to;
      if (best !== current) {
        current = best;
        for (var j = 0; j < steps.length; j++) {
          steps[j].classList.toggle('is-current', j === best);
          layers[j].classList.toggle('is-active', j === best);
        }
      }

      var lateralVelocity = targetX - currentX;
      var rotation = Math.max(-2.2, Math.min(2.2, lateralVelocity * 0.045)) +
        Math.sin(now / 2300) * 0.22;
      var floatY = Math.sin(now / 1750) * (window.innerWidth <= 760 ? 1.5 : 3);
      var signature = currentX.toFixed(2) + '|' + currentY.toFixed(2) + '|' +
        rotation.toFixed(3) + '|' + floatY.toFixed(2);
      if (signature === last) return;

      last = signature;
      traveler.style.setProperty('--bb-ritual-traveler-x', currentX.toFixed(2) + 'px');
      traveler.style.setProperty('--bb-ritual-traveler-y', (currentY + floatY).toFixed(2) + 'px');
      stage.style.setProperty('--bb-ritual-traveler-rotate', rotation.toFixed(3) + 'deg');
    });
  }

  /* ---------------------------------------------------------------- boot */
  function boot() {
    initReveals();
    initSplits();
    initScrub();
    initHeroZoom();
    initHeaderRecolour();
    initActivesMotion();
    initRitual();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  // Theme editor re-renders sections without a page load.
  document.addEventListener('shopify:section:load', boot);
})();
